import { IconButton, MenuItem, Select, TextField } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import CustomButton from '../reusable/CustomButton';
import Panel from '../reusable/Panel';
import AddIcon from '../assets/eoa-configuration-add.svg';
import { digitsOnlyError } from './fieldValidation';
import {
  AcademicsForm,
  addAlias,
  addAliasMapping,
  addPeriodName,
  namedPeriodOptions,
  removeAlias,
  removeAliasMapping,
  removePeriodName,
  setTermCount,
  uniqueStrings,
  updateAlias,
  updateAliasMappingPeriod,
  updatePeriodName,
} from './academicsForm';

interface AcademicsFieldsProps {
  value: AcademicsForm;
  onChange: (value: AcademicsForm) => void;
}

const placeholder = 'Your text here';

const AcademicsFields = ({ value, onChange }: AcademicsFieldsProps) => {
  const periodOptions = namedPeriodOptions(value);
  const termCountHelperText = digitsOnlyError(value.termCount);

  return (
    <>
      <Panel className="eoa-configuration-card">
        <h3 className="eoa-configuration-card-title">Academic periods</h3>
        <div className="eoa-configuration-fields">
          <div className="eoa-configuration-field eoa-configuration-field--term-count">
            <span className="eoa-configuration-field-label">What is your academic term count?</span>
            <TextField
              placeholder="Number"
              value={value.termCount}
              error={Boolean(termCountHelperText)}
              helperText={termCountHelperText}
              onChange={(event) => onChange(setTermCount(value, event.target.value))}
              inputProps={{ inputMode: 'numeric' }}
            />
          </div>

          <div className="eoa-configuration-field">
            <span className="eoa-configuration-field-label">Academic period names</span>
            <div className="eoa-configuration-removable-list">
              {value.periodNames.map((period) => (
                <div key={period.id} className="eoa-configuration-removable-row">
                  <TextField
                    className="eoa-configuration-period-name-input"
                    placeholder={placeholder}
                    value={period.name}
                    onChange={(event) => onChange(updatePeriodName(value, period.id, event.target.value))}
                  />
                  <IconButton
                    className="eoa-configuration-remove-button"
                    aria-label="Remove academic period name"
                    onClick={() => onChange(removePeriodName(value, period.id))}
                  >
                    <CloseIcon />
                  </IconButton>
                </div>
              ))}
              <button
                type="button"
                className="eoa-configuration-add-link"
                onClick={() => onChange(addPeriodName(value))}
              >
                <img src={AddIcon} alt="" width={18} height={18} />
                Add more
              </button>
            </div>
          </div>
        </div>
      </Panel>

      <Panel className="eoa-configuration-card">
        <h3 className="eoa-configuration-card-title">Aliases</h3>
        <div className="eoa-configuration-fields">
          <div className="eoa-configuration-field">
            <span className="eoa-configuration-field-label">
              If academic periods ever show up in your data under different names, what are the aliases that exist in
              the system?
            </span>
            <div className="eoa-configuration-alias-mappings">
              {value.aliasMappings.map((mapping) => {
                const aliasPeriodOptions = uniqueStrings([
                  mapping.periodName,
                  ...periodOptions.filter(
                    (option) =>
                      !value.aliasMappings.some(
                        (otherMapping) => otherMapping.id !== mapping.id && otherMapping.periodName === option
                      )
                  ),
                ]);

                return (
                  <div key={mapping.id} className="eoa-configuration-alias-mapping">
                    <div className="eoa-configuration-alias-mapping-header">
                      <Select
                        className="eoa-configuration-alias-period-select"
                        variant="standard"
                        displayEmpty
                        value={mapping.periodName}
                        onChange={(event) => onChange(updateAliasMappingPeriod(value, mapping.id, event.target.value))}
                        renderValue={(selected) => selected || 'Value'}
                      >
                        {aliasPeriodOptions.length ? (
                          aliasPeriodOptions.map((option) => (
                            <MenuItem key={option} value={option}>
                              {option}
                            </MenuItem>
                          ))
                        ) : (
                          <MenuItem disabled>No options</MenuItem>
                        )}
                      </Select>
                      <span className="eoa-configuration-maps-to">maps to</span>
                      <IconButton
                        className="eoa-configuration-remove-button"
                        aria-label="Remove academic period alias mapping"
                        onClick={() => onChange(removeAliasMapping(value, mapping.id))}
                      >
                        <CloseIcon />
                      </IconButton>
                    </div>
                    <div className="eoa-configuration-alias-list">
                      {mapping.aliases.map((alias) => (
                        <div key={alias.id} className="eoa-configuration-removable-row">
                          <TextField
                            className="eoa-configuration-period-name-input"
                            placeholder="Alias"
                            value={alias.value}
                            onChange={(event) => onChange(updateAlias(value, mapping.id, alias.id, event.target.value))}
                          />
                          <IconButton
                            className="eoa-configuration-remove-button"
                            aria-label="Remove alias"
                            onClick={() => onChange(removeAlias(value, mapping.id, alias.id))}
                          >
                            <CloseIcon />
                          </IconButton>
                        </div>
                      ))}
                      <button
                        type="button"
                        className="eoa-configuration-add-link"
                        onClick={() => onChange(addAlias(value, mapping.id))}
                      >
                        <img src={AddIcon} alt="" width={18} height={18} />
                        Add alias
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
            <div className="eoa-configuration-alias-footer">
              <CustomButton
                className="eoa-configuration-add-period-button"
                variant="secondary"
                size="medium"
                buttonText="Add new academic period"
                icon={<img src={AddIcon} alt="" width={18} height={18} />}
                iconPosition="start"
                buttonAction={() => onChange(addAliasMapping(value))}
              />
            </div>
          </div>
        </div>
      </Panel>

      <Panel className="eoa-configuration-card">
        <h3 className="eoa-configuration-card-title">Additional information</h3>
        <div className="eoa-configuration-fields">
          <div className="eoa-configuration-field">
            <span className="eoa-configuration-field-label">
              Is there significant cross-school enrollment in your district?
            </span>
            <TextField
              fullWidth
              multiline
              minRows={4}
              placeholder={placeholder}
              value={value.crossSchoolEnrollment}
              onChange={(event) => onChange({ ...value, crossSchoolEnrollment: event.target.value })}
            />
          </div>
          <div className="eoa-configuration-field">
            <span className="eoa-configuration-field-label">
              How do you handle student transfers? Do they have previous school information in their transcript file?
            </span>
            <TextField
              fullWidth
              multiline
              minRows={4}
              placeholder={placeholder}
              value={value.studentTransfers}
              onChange={(event) => onChange({ ...value, studentTransfers: event.target.value })}
            />
          </div>
          <div className="eoa-configuration-field">
            <span className="eoa-configuration-field-label">
              How do dual-enrollment courses show up in the transcripts?
            </span>
            <TextField
              fullWidth
              multiline
              minRows={4}
              placeholder={placeholder}
              value={value.dualEnrollment}
              onChange={(event) => onChange({ ...value, dualEnrollment: event.target.value })}
            />
          </div>
        </div>
      </Panel>
    </>
  );
};

export default AcademicsFields;
